import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PromisePool from 'es6-promise-pool';
import { connect } from 'react-redux';
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { updateFile } from '../../actions/Files';

import { APIcreateFilesGroup } from '../../api/Files';

import ProcessingSpinner from '../ProcessingSpinner';
import ErrorBoundary from '../ErrorBoundary';
import FileWatermarkedSelect from '../Upload/FileWatermarkedSelect';

const WatermarkedModal = ({
  files,
  handleClose,
  updateFileAction,
}) => {
  const intl = useIntl();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [saveButtonText, setSaveButtonText] = useState('Save');
  const [savedFiles, setSavedFiles] = useState(0);
  const [watermarked, setWatermarked] = useState(true);

  const canBeWatermarkedFiles = files.filter(f => f.can_be_watermarked);
  const filesCount = files.length;

  useEffect(() => {
    if (saving && filesCount > 1) {
      setSaveButtonText(intl.formatMessage(
        { defaultMessage: 'Saved {savedFiles} of {filesCount} files...' },
        { savedFiles, filesCount: canBeWatermarkedFiles.length },
      ));
    } else {
      setSaveButtonText(intl.formatMessage({ defaultMessage: 'Save' }));
    }
  }, [saving, savedFiles, filesCount, canBeWatermarkedFiles.length, intl]);

  const handleChange = (value) => {
    setWatermarked(value);
  };

  const handleSaveFiles = (uploadUuid) => {
    let counter = 0;
    const promiseProducer = () => {
      if (counter < canBeWatermarkedFiles.length) {
        const file = canBeWatermarkedFiles[counter];
        counter += 1;
        file.watermarked = watermarked;
        file.uploadUuid = uploadUuid;

        return updateFileAction(file).then(() => {
          setSavedFiles(counter);
        });
      }
      return null;
    };

    const pool = new PromisePool(promiseProducer, 2);
    pool.start().then(() => {
      setSaving(false);
      setSavedFiles(0);
      if (!error) {
        handleClose();
      }
    });
  };

  const handleSubmit = () => {
    setError(false);
    setSaving(true);

    APIcreateFilesGroup({ type: 'edit_files' }).then((response) => {
      const { data } = response;
      if (data) {
        handleSaveFiles(data.uuid);
      }
    });
  };

  const getLabel = () => {
    if (filesCount === canBeWatermarkedFiles.length) {
      return intl.formatMessage({
        defaultMessage: `You are about to change watermark for {filesCount} 
        {filesCount, plural, one {file} other {files}}.`,
      }, { filesCount });
    }
    return intl.formatMessage({
      defaultMessage: `Only images can be watermarked. You are about to change 
        watermark for {canBeWatermarkedFiles} of {filesCount} selected 
        {filesCount, plural, one {file} other {files}}.`,
    }, { canBeWatermarkedFiles: canBeWatermarkedFiles.length, filesCount });
  };

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {intl.formatMessage({ defaultMessage: 'Add or remove file watermark' })}
      </ModalHeader>
      <ModalBody className="modal-status-body">
        {filesCount > 1 && (
          <p className="modal-body-center">
            {getLabel()}
          </p>
        )}
        <FileWatermarkedSelect
          isEdit={true}
          handleChange={handleChange}
          watermarked={watermarked}
        />
        {error && (
          <div className="invalid-feedback" style={{ display: 'block' }}>
            {intl.formatMessage({ defaultMessage: 'An error occured. Please try again later.' })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose}>
          {intl.formatMessage({ defaultMessage: 'Close' })}
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={saving}>
          <ProcessingSpinner show={saving} /> {saveButtonText}
        </Button>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  statuses: state.Files.statuses,
});

const mapDispatchToProps = dispatch => ({
  updateFileAction: file => (
    dispatch(updateFile(file))
  ),
});

WatermarkedModal.propTypes = {
  files: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  updateFileAction: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(WatermarkedModal);
